import { useEffect, useState } from 'react';

import Typography from '~/shared/components/Typography';

interface CircularProgressProps {
  percentage: number;
  size?: number; // Diameter of the circle
  strokeWidth?: number;
  animationDuration?: number; // Duration in milliseconds
  loading?: boolean; // When true, hide numeric percentage
}

export default function CircularProgress({
  percentage,
  size = 96,
  strokeWidth = 6,
  animationDuration = 1000,
  loading = false,
}: CircularProgressProps) {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (animatedPercentage / 100) * circumference;

  useEffect(() => {
    const startTime = Date.now();
    const startPercentage = 0;
    const endPercentage = percentage;

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);

      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentPercentage = startPercentage + (endPercentage - startPercentage) * easeOut;

      setAnimatedPercentage(currentPercentage);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [percentage, animationDuration]);

  const gradientId = `progressGradient-${size}-${strokeWidth}`; // Unique ID for the gradient

  return (
    <div className="relative grid place-items-center " style={{ width: size, height: size }}>
      <svg
        className="absolute top-0 left-0"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#04C3A6" />
            <stop offset="50%" stopColor="#42E196" />
            <stop offset="100%" stopColor="#04C3A6" />
          </linearGradient>
        </defs>
        {/* Background Circle */}
        <circle
          className="text-gray-200"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {/* Progress Circle */}
        <circle
          className="transform rotate-180 origin-center"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      {/* Percentage Text */}
      {!loading && (
        <Typography variant="H5" color="text-primary-900" className="font-family-inter">
          {`${Math.round(animatedPercentage)}%`}
        </Typography>
      )}
    </div>
  );
}
